import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, Flame, FileText, Calculator, ArrowRight, Info } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CalcBanner from "@/components/CalcBanner";
import { track, SITE_URL, scrollToId } from "@/lib/api";

const META_TITLE = "Zákony a normy pri zateplení 2026 — tepelný odpor, požiarna bezpečnosť | Zateplenia.eu";
const META_DESC = "Aktuálne normy pre zateplenie fasády 2026: STN 73 0540 (tepelný odpor R = 4,4), požiarna bezpečnosť STN 73 0802, zákony a dotácie. Vypočítajte si hrúbku izolácie, ktorá norme vyhovuje.";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

const R_ROWS = [
  ["Fasáda (vonkajšia stena)", "4,4", "6,5"],
  ["Plochá alebo šikmá strecha do 45°", "6,5", "9,9"],
  ["Strop nad vonkajším prostredím", "6,5", "9,8"],
  ["Strop pod nevykurovanou povalou", "4,9", "6,5"],
];

const MATERIALS_L = [
  { key: "eps", name: "EPS 70F (biely polystyrén)", lambda: 0.040 },
  { key: "grafit", name: "Grafitový EPS (šedý)", lambda: 0.031 },
  { key: "vata", name: "Minerálna vata", lambda: 0.037 },
];
const WALLS_R = [
  { key: "tehla", name: "Plná tehla 30–44 cm", r: 0.9 },
  { key: "porobeton", name: "Pórobetón", r: 2.0 },
  { key: "panel", name: "Beton / panel", r: 0.15 },
  { key: "drevo", name: "Drevostavba", r: 1.5 },
];

const FAQS = [
  { q: "Aká hrúbka zateplenia vyhovuje norme v roku 2026?",
    a: "Pre fasádu platí požadovaný tepelný odpor R = 4,4 m²K/W (norma STN 73 0540). Pri bežnom tehlovom dome to znamená približne 140 mm bieleho EPS alebo 110 mm grafitového EPS. Presnú hrúbku vždy počíta projektant podľa skladby vašej steny." },
  { q: "Sú požiarné zábrany pri polystyréne (EPS) povinné?",
    a: "Pri bytových domoch áno — nad každým podlažím s otvormi sa navrhujú pásy z nehorľavej minerálnej vlny široké aspoň 200 mm (STN 73 0802). Pri obnove rodinného domu do požiarnej výšky 22,5 m možno EPS spravidla použiť aj bez zábran; konečné posúdenie má projektant. Pri minerálnej vate zábrany netreba vôbec." },
  { q: "Menili sa normy na zateplenie v posledných rokoch?",
    a: "Áno. Tepelnotechnická norma STN 73 0540 platí v konsolidovanom znení z roku 2019 (prísnejšie hodnoty z roku 2021 sú odporúčané, nie povinné) a požiarna norma STN 73 0802 bola aktualizovaná zmenou Z3 v roku 2022. Staršie články na internete preto často uvádzajú zastarané údaje." },
  { q: "Potrebujem na zateplenie domu stavebné povolenie?",
    a: "Bežné zateplenie fasády rodinného domu sa spravidla rieši bez stavebného povolenia. Inak je to pri pamiatkovo chránených objektoch, v pamiatkových zónach a pri zložitejších zásahoch — odporúčame krátku konzultáciu so stavebným úradom alebo projektantom." },
];

export default function Normy() {
  const [mat, setMat] = useState("eps");
  const [wall, setWall] = useState("tehla");

  useEffect(() => {
    document.title = META_TITLE;
    setMeta("description", META_DESC);
    setCanonical(`${SITE_URL}/normy`);
    track("visit", "normy");
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
    };
    let s = document.getElementById("ld-json");
    if (!s) { s = document.createElement("script"); s.id = "ld-json"; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
  }, []);

  const result = useMemo(() => {
    const m = MATERIALS_L.find((x) => x.key === mat);
    const w = WALLS_R.find((x) => x.key === wall);
    const calc = (target) => Math.min(400, Math.max(0, Math.ceil(((target - w.r) * m.lambda * 1000) / 10) * 10));
    return { required: calc(4.4), target: calc(6.5), material: m.name, wall: w.name };
  }, [mat, wall]);

  return (
    <div className="bg-ink min-h-screen" data-testid="normy-page">
      <Header />

      <section className="pt-36 pb-14">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <div className="inline-flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-6">
            <Scale className="w-4 h-4" /> Legislatíva · aktuálne k 2026
          </div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-white leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="normy-title">
            Zákony a normy<br /><span className="text-gold">pri zateplení v roku 2026</span>
          </motion.h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">
            Zrozumiteľný prehľad platných pravidiel: koľko izolácie vyžaduje norma, kedy sú povinné požiarne zábrany
            a na čo si dať pozor pri dotáciách. Bez odborného žargónu, s prepočtom na vašu stenu.
          </p>
        </div>
      </section>

      {/* Tepelnotechnická norma */}
      <section className="py-14 border-t border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <div className="flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-4">
            <FileText className="w-4 h-4" /> STN 73 0540 · tepelnotechnická norma
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-white mb-5">Aký tepelný odpor musí konštrukcia mať?</h2>
          <p className="text-white/60 leading-relaxed max-w-3xl">
            Norma STN 73 0540 (konsolidované znenie 2019) určuje požadované hodnoty tepelného odporu R (m²K/W).
            Čím vyššie číslo, tým lepšie stena zadržiava teplo. Prísnejšie hodnoty plánované od roku 2021 sa stali
            <strong className="text-white"> odporúčanými, nie povinnými</strong> — oplatí sa však mieriť na ne,
            pretože úspora sa ráta na celú životnosť fasády.
          </p>
          <div className="mt-8 border border-white/10 overflow-x-auto" data-testid="normy-r-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="text-left px-5 py-3 font-normal">Konštrukcia</th>
                  <th className="text-right px-5 py-3 font-normal">Požadovaná R (m²K/W)</th>
                  <th className="text-right px-5 py-3 font-normal">Odporúčaná cieľová R</th>
                </tr>
              </thead>
              <tbody>
                {R_ROWS.map(([k, req, tgt]) => (
                  <tr key={k} className="border-t border-white/5">
                    <td className="px-5 py-3 text-white/80">{k}</td>
                    <td className="px-5 py-3 text-right font-mono2 text-white">{req}</td>
                    <td className="px-5 py-3 text-right font-mono2 text-gold">{tgt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Interaktívny prepočet */}
          <div className="mt-10 border border-gold/30 bg-gold/5 p-6 md:p-8" data-testid="normy-calc">
            <div className="flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-4">
              <Calculator className="w-4 h-4" /> Ilustratívny prepočet hrúbky
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-mono2 uppercase tracking-widest">Materiál izolácie</label>
                <select value={mat} onChange={(e) => setMat(e.target.value)} data-testid="normy-calc-material"
                  className="mt-1 w-full bg-ink border border-white/15 px-4 py-3 text-white text-sm focus:border-gold outline-none">
                  {MATERIALS_L.map((m) => <option key={m.key} value={m.key} className="bg-ink">{m.name} (λ = {m.lambda.toFixed(3).replace(".", ",")})</option>)}
                </select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono2 uppercase tracking-widest">Vaša súčasná stena</label>
                <select value={wall} onChange={(e) => setWall(e.target.value)} data-testid="normy-calc-wall"
                  className="mt-1 w-full bg-ink border border-white/15 px-4 py-3 text-white text-sm focus:border-gold outline-none">
                  {WALLS_R.map((w) => <option key={w.key} value={w.key} className="bg-ink">{w.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <div className="border border-white/10 bg-ink p-5">
                <div className="text-white/50 text-xs uppercase tracking-widest font-mono2 mb-1">Minimum podľa normy (R = 4,4)</div>
                <div className="font-display text-4xl text-white" data-testid="normy-calc-required">{result.required} mm</div>
              </div>
              <div className="border border-gold/40 bg-ink p-5">
                <div className="text-gold text-xs uppercase tracking-widest font-mono2 mb-1">Odporúčané pre maximálnu úsporu (R = 6,5)</div>
                <div className="font-display text-4xl text-gold" data-testid="normy-calc-target">{result.target} mm</div>
              </div>
            </div>
            <p className="text-white/40 text-xs mt-4 flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              Ilustratívny prepočet (hrúbka = chýbajúci tepelný odpor × λ materiálu). Presný výpočet skladby steny robí projektant podľa STN 73 0540 a STN EN ISO 6946.
            </p>
            <Link to="/#kalkulacka" onClick={() => setTimeout(() => scrollToId("#kalkulacka"), 60)} data-testid="normy-calc-cta"
              className="mt-5 inline-flex items-center gap-2 bg-gold text-ink px-7 py-3.5 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors">
              Spočítať cenu tejto hrúbky <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Požiarna bezpečnosť */}
      <section className="py-14 border-t border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <div className="flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-4">
            <Flame className="w-4 h-4" /> STN 73 0802 (Z3:2022) · požiarna bezpečnosť
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-white mb-5">Kedy sú pri zateplení povinné požiarné zábrany?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="border border-white/10 bg-ink-2 p-6">
              <h3 className="text-white font-semibold text-lg mb-3">Bytové domy</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Pri horľavom izolante (napr. EPS) sa nad každým podlažím s otvormi navrhujú <strong className="text-white">pásy z nehorľavej minerálnej vlny široké aspoň 200 mm</strong>, ktoré bránia šíreniu plameňa po fasáde. Riešenie vždy definuje projekt požiarnej ochrany.
              </p>
            </div>
            <div className="border border-white/10 bg-ink-2 p-6">
              <h3 className="text-white font-semibold text-lg mb-3">Rodinné domy</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Pri obnove rodinného domu s požiarnou výškou do 22,5 m možno ETICS s EPS spravidla použiť <strong className="text-white">aj bez požiarnych zábran</strong>; pri novostavbách a špecifických stavbách (napr. veľká garáž) rozhoduje projektant. Zvolíte-li minerálnu vatu (nehorľavá trieda A2), zábrany netreba vôbec.
              </p>
            </div>
          </div>
          <p className="text-white/60 text-sm leading-relaxed mt-6 max-w-3xl">
            Pomáha vám rozhodnúť sa medzi EPS a vatou? Pozrite si <Link to="/materialy" className="text-gold underline hover:text-gold-light">porovnanie materiálov</Link> a <Link to="/blog/kolko-stoji-zateplenie-2026" className="text-gold underline hover:text-gold-light">cenovú štúdiu 2026</Link>.
          </p>
        </div>
      </section>

      {/* Zákony a dotácie */}
      <section className="py-14 border-t border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <div className="flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-4">
            <Scale className="w-4 h-4" /> Zákony a štátna podpora
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-white mb-5">Na čo sa normy viažu?</h2>
          <div className="space-y-5 max-w-3xl">
            <div className="border-l-2 border-gold/50 pl-5">
              <h3 className="text-white font-semibold">Zákon č. 555/2005 Z. z. o energetickej hospodárnosti budov</h3>
              <p className="text-white/60 text-sm leading-relaxed mt-1">Základné pravidlá energetickej hospodárnosti budov vrátane požiadaviek na obnovu. Novela č. 96/2019 Z. z. upravila podmienky štátnej podpory zatepľovania a podporu domov s takmer nulovou spotrebou energie.</p>
            </div>
            <div className="border-l-2 border-gold/50 pl-5">
              <h3 className="text-white font-semibold">Zákon č. 150/2013 Z. z. — Štátny fond rozvoja bývania</h3>
              <p className="text-white/60 text-sm leading-relaxed mt-1">Rámec pre príspevky na zateplenie rodinných a bytových domov a ďalšie formy modernizácie bývania.</p>
            </div>
            <div className="border-l-2 border-gold/50 pl-5">
              <h3 className="text-white font-semibold">Dotácia Obnov dom</h3>
              <p className="text-white/60 text-sm leading-relaxed mt-1">Pri rekonštrukcii staršieho rodinného domu môžete získať dotáciu, ktorá pokryje významnú časť nákladov — podmienkou je práve splnenie tepelnotechnických noriem. <Link to="/blog/dotacia-obnov-dom-2026-ako-ziskat-prispevok-na-zateplenie-rodinneho-do" className="text-gold underline hover:text-gold-light">Čítajte návod, ako získať príspevok</Link>.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 border-t border-white/10">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Časté otázky</p>
          <h2 className="font-display text-3xl md:text-4xl text-white mb-10">Normy pri zateplení — FAQ</h2>
          <div className="divide-y divide-white/10">
            {FAQS.map((f, i) => (
              <div key={f.q} className="py-6" data-testid={`normy-faq-${i}`}>
                <h3 className="text-white font-semibold text-lg mb-2">{f.q}</h3>
                <p className="text-white/60 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <p className="text-white/40 text-xs mt-8 leading-relaxed">
            Obsah tejto stránky je informatívny a nenahrádza projektovú dokumentáciu ani právne poradenstvo. Záväzné znenia noriem a zákonov nájdete v Slov-lex a u Slovenského úradu technickej normalizácie.
          </p>
          <div className="mt-10"><CalcBanner context="normy" /></div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
